"use client";

import { useEffect, useState } from "react";

type EventoInstalar = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

declare global {
  interface Window {
    __vecippInstalable: EventoInstalar | null;
  }
}

/** iOS no tiene forma de instalar por código: Apple no expone nada equivalente. Ahí no queda otra. */
function esIOS(): boolean {
  const ua = navigator.userAgent;
  // El iPad moderno se identifica como Mac; se distingue porque tiene pantalla táctil.
  return /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export type EstadoInstalacion = "cargando" | "instalable" | "instalada" | "ios" | "sin-soporte";

/**
 * **El botón que instala la app de verdad, sin que la persona haga nada.**
 *
 * Cuando el navegador lo permite, un toque abre el diálogo del sistema y la app queda en la pantalla de
 * inicio. Las instrucciones a mano solo aparecen donde **no hay otra opción**:
 *
 * | Dónde | Qué pasa |
 * |---|---|
 * | Android (Chrome, Edge, Samsung) | Botón real: un toque y se instala |
 * | iPhone / iPad (Safari) | ⚠️ Apple no permite instalar por código — instrucciones |
 * | Ya instalada | No se muestra nada |
 */
export function BotonInstalar({ onEstado }: { onEstado?: (e: EstadoInstalacion) => void }) {
  const [estado, setEstado] = useState<EstadoInstalacion>("cargando");
  const [instalando, setInstalando] = useState(false);

  useEffect(() => {
    const yaInstalada =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as { standalone?: boolean }).standalone === true;

    const calcular = () => {
      if (yaInstalada) return setEstado("instalada");
      if (window.__vecippInstalable) return setEstado("instalable");
      if (esIOS()) return setEstado("ios");
      // Puede que el evento aún no haya llegado; se deja en "sin-soporte" y el listener lo corrige.
      return setEstado("sin-soporte");
    };

    calcular();
    const alSerInstalable = () => setEstado("instalable");
    const alInstalarse = () => setEstado("instalada");
    window.addEventListener("vecipp:instalable", alSerInstalable);
    window.addEventListener("vecipp:instalada", alInstalarse);
    return () => {
      window.removeEventListener("vecipp:instalable", alSerInstalable);
      window.removeEventListener("vecipp:instalada", alInstalarse);
    };
  }, []);

  useEffect(() => {
    if (estado !== "cargando") onEstado?.(estado);
  }, [estado, onEstado]);

  async function instalar() {
    const evento = window.__vecippInstalable;
    if (!evento) return;
    setInstalando(true);
    await evento.prompt();
    const { outcome } = await evento.userChoice;
    // El evento se consume: Chrome no deja volver a usarlo. Si dijo que no, se vuelve al estado normal.
    window.__vecippInstalable = null;
    setInstalando(false);
    setEstado(outcome === "accepted" ? "instalada" : "sin-soporte");
  }

  if (estado === "instalada") {
    return (
      <p className="rounded-2xl border border-emerald-600/40 bg-emerald-600/10 px-5 py-4 text-center font-semibold text-emerald-600">
        Ya tienes Vecipp instalada en este dispositivo
      </p>
    );
  }

  if (estado === "instalable") {
    return (
      <button
        type="button"
        onClick={instalar}
        disabled={instalando}
        className="w-full rounded-2xl bg-primary px-5 py-4 text-center font-semibold text-lg text-primary-foreground disabled:opacity-50"
      >
        {instalando ? "Instalando…" : "Instalar ahora"}
      </button>
    );
  }

  return null;
}
